import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'
import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useSelector from '@hooks/useSelector'
import useReduxAction from '@hooks/useReduxAction'

import type { EntityOptions } from '@redux/modules/module'

import type { BrandGroupModel, BrandGroupPublicationModel, PublicationModel } from '@models/types'

const watchEntityKeys = ['brandGroupPublications']

type BrandGroupFilters = {
  brand_group_id?: number,
  brand_id?: number,
  publication_id?: number,
  string?: string,
}

type Options = {
  entityKey?: string,
  filters?: BrandGroupFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

interface UseBrandGroupPublicationsPayload {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredBrandGroupPublications: BrandGroupPublicationModel[],
  filteredBrandGroupPublicationsCount: number,
  hasFilteredBrandGroupPublications: boolean,
  loading: boolean,
}

function useBrandGroupPublications(options: Options = {}): UseBrandGroupPublicationsPayload {
  const {
    entityKey, filters, requestOptions,
  } = options

  const {
    brand_group_id: filterBrandGroupId,
    brand_id: filterBrandId,
    publication_id: filterPublicationId,
    string: filterString,
  } = filters || {}

  const {
    updatedEntities: { brandGroupPublications: brandGroupPublicationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const { brandGroups, brandGroupPublications, publications } = entities || {}

  const filteredBrandGroupPublications = useMemo((): BrandGroupPublicationModel[] => {
    const filtered = Object.values(brandGroupPublications).filter((brandGroupPublication) => {
      const { brand_group_id, publication_id } = brandGroupPublication

      const publication: Partial<PublicationModel> = publications[publication_id] || {}
      const { title: publicationTitle } = publication

      const brandGroup: Partial<BrandGroupModel> = brandGroups[brand_group_id] || {}
      const { brand_id } = brandGroup

      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      const matchBrandGroup = matchFilterNumber(brand_group_id, filterBrandGroupId)
      const matchPublication = matchFilterNumber(publication_id, filterPublicationId)
      const matchPublicationTitle = matchFilterString(publicationTitle, filterString)

      return matchBrand && matchBrandGroup && matchPublicationTitle && matchPublication
    })

    return sortArrayBy(
      filtered,
      'asc',
      ({ publication_id }) => publications[publication_id]?.title,
    )
  }, [brandGroupPublicationsUpdatedAt, JSON.stringify(filters)])

  const filteredBrandGroupPublicationsCount = filteredBrandGroupPublications.length
  const hasFilteredBrandGroupPublications = !!filteredBrandGroupPublicationsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandGroupPublicationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandGroupPublications } = useReduxAction(
    'brandGroupPublications',
    'loadBrandGroupPublications',
    {
      entityKey,
      limit,
      ...requestOptions,
      ...filtersWithOffset,
    },
    [filters, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandGroupPublications,
    filteredBrandGroupPublicationsCount,
    hasFilteredBrandGroupPublications,
    loading: loadingBrandGroupPublications,
  }
}

export default useBrandGroupPublications
